<template>
  <form class="space-y-6" @submit.prevent="save">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <FormInput
        v-model="billingInfo.info.fiscal_id"
        :label="t('fiscalIdLabel')"
        :error="v$.info.fiscal_id.$error ? getFiscalIdErrorMessage(v$.info.fiscal_id.$errors) : undefined"
        placeholder="00-00000000-0"
      />
      <FormInput
        v-model="billingInfo.info.name"
        :label="t('legalNameLabel')"
        :placeholder="t('legalNamePlaceholder')"
        :error="v$.info.name.$error ? t('fieldRequired') : undefined"
      />
      <FormInput
        v-model="billingInfo.email"
        :label="t('emailLabel')"
        placeholder="example@gmail.com"
        :error="v$.email.$error ? getEmailErrorMessage(v$.email.$errors) : undefined"
      />
    </div>
    <div class="flex justify-end space-x-2">
      <SimpleButton @click="close" theme="white">{{ t("close") }}</SimpleButton>
      <SimpleButton type="submit" :loading="loadingSave">
        <template #leading>
          <LockClosedIcon />
        </template>
        {{ t("save") }}</SimpleButton
      >
    </div>
  </form>
</template>
<script lang="ts" setup>
import { reactive, watch, ref } from "vue";

// Component
import SimpleButton from "@atoms/SimpleButton.vue";
import FormInput from "@molecules/FormInput.vue";
import { LockClosedIcon } from "@heroicons/vue/solid";

// I18n
import { useI18n } from "vue-i18n";

// Utils
import { cloneDeep } from "lodash";
import useVuelidate from "@vuelidate/core";
import type { ErrorObject } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";

// Composables
import { useNotifications } from "@composables/notifications";

// Service
import { useBillingInfoService } from "@services";
import type { ArgentinaBillingInfoData } from "@domain/billingInfo";

const { t } = useI18n();
const billingInfoService = useBillingInfoService();

const { notify } = useNotifications();

const props = withDefaults(
  defineProps<{
    billingData: ArgentinaBillingInfoData;
  }>(),
  {},
);

const emit = defineEmits<{
  close: [];
  updateBillingData: [ArgentinaBillingInfoData];
}>();

const close = () => {
  emit("close");
  fillBillingInfo(props.billingData);
  v$.value.$reset();
};

const updateBillingData = (info: ArgentinaBillingInfoData) => {
  emit("updateBillingData", info);
};

const billingInfo = reactive<ArgentinaBillingInfoData>({
  country: "ar",
  email: "",
  info: {
    fiscal_id: "",
    fiscal_id_clean: "",
    name: "",
  },
});

// Test formato CUIT / CUIL
const testFiscalId = (value: string) => {
  const regex = new RegExp(/\b(20|23|24|27|30|33|34)-[0-9]{8}-[0-9]/g);
  return regex.test(value);
};

// Test CUIT / CUIL real
const testCuitVerifDigit = (cuitFormatted: string) => {
  const cuit_nro = cuitFormatted.replaceAll("-", "");
  let rv = false;
  let resultado = 0;
  const codes = "6789456789";
  let verificador = parseInt(cuit_nro[cuit_nro.length - 1]);
  let x = 0;

  while (x < 10) {
    let digitoValidador = parseInt(codes.substring(x, x + 1));
    if (isNaN(digitoValidador)) digitoValidador = 0;
    let digito = parseInt(cuit_nro.substring(x, x + 1));
    if (isNaN(digito)) digito = 0;
    let digitoValidacion = digitoValidador * digito;
    resultado += digitoValidacion;
    x++;
  }
  resultado = resultado % 11;
  rv = resultado == verificador;
  return rv;
};

const validationRules = {
  country: { required },
  email: { required, email },
  info: {
    fiscal_id: {
      required,
      testFiscalId,
      testCuitVerifDigit,
    },
    name: {
      required,
    },
  },
};

const v$ = useVuelidate(validationRules, billingInfo);

const getEmailErrorMessage = (errors: ErrorObject[]): string | undefined => {
  if (errors.length === 0) return undefined;

  const error = errors[0];
  if (error.$validator === "required") return t("fieldRequired");
  return t("invalidEmail");
};

const getFiscalIdErrorMessage = (errors: ErrorObject[]): string | undefined => {
  if (errors.length === 0) return undefined;

  const error = errors[0];
  if (error.$validator === "required") return t("fieldRequired");
  return t("invalidFiscalId");
};

// Reset de validation vuelidate
watch(
  () => billingInfo,
  (newValue, oldValue) => {
    for (const key in newValue) {
      if (newValue[key] !== oldValue[key]) {
        v$[key].$reset();
      }
    }
  },
  { deep: true },
);

// Reset de validation vuelidate
watch(
  () => cloneDeep(billingInfo.info),
  (newInfo, oldInfo) => {
    for (const key in newInfo) {
      if (newInfo[key] !== oldInfo[key]) {
        v$.value.info?.[key]?.$reset();
      }
    }
  },
  { deep: true },
);

const loadingSave = ref(false);

const save = async () => {
  const validation = await v$.value.$validate();
  if (!validation) return;

  loadingSave.value = true;
  try {
    await billingInfoService.save({
      country: "ar",
      email: billingInfo.email,
      fiscal_id: billingInfo.info.fiscal_id ?? "",
      fiscal_id_clean: billingInfo.info.fiscal_id.replaceAll("-", ""),
      name: billingInfo.info.name,
    });

    notify({ title: t("notify.success") });
    updateBillingData(billingInfo);
    emit("close");
  } finally {
    loadingSave.value = false;
  }
};

const fillBillingInfo = (data: ArgentinaBillingInfoData) => {
  billingInfo.email = data.email;

  if (data.country !== "ar") return;

  billingInfo.info.fiscal_id = data.info.fiscal_id ?? "";
  billingInfo.info.fiscal_id_clean = data.info.fiscal_id_clean ?? "";
  billingInfo.info.name = data.info.name ?? "";
};

watch(
  () => props.billingData,
  () => {
    fillBillingInfo(props.billingData);
  },
  { deep: true },
);
</script>

<i18n lang="json">
{
  "es": {
    "close": "Cancelar",
    "save": "Guardar cambios",
    "emailLabel": "Email administrativo",
    "fiscalIdLabel": "CUIT o CUIL",
    "legalNameLabel": "Nombre / Razón social",
    "legalNamePlaceholder": "Compañia S.R.L.",
    "fieldRequired": "Campo requerido.",
    "invalidFiscalId": "El CUIT o CUIL es inválido. Utiliza formato 00-00000000-0",
    "invalidEmail": "El email ingresado es inválido.",
    "notify": {
      "success": "Información actualizada con éxito"
    }
  },
  "pt": {
    "close": "Cancelar",
    "save": "Salvar alterações",
    "emailLabel": "E-mail administrativo",
    "fiscalIdLabel": "CUIT ou CUIL",
    "legalNameLabel": "Nome / Razão social",
    "legalNamePlaceholder": "Empresa S.A.",
    "fieldRequired": "Campo obrigatório",
    "invalidFiscalId": "O CUIT ou CUIL é inválido. Utiliza formato 00-00000000-0",
    "invalidEmail": "O e-mail inserido é inválido",
    "notify": {
      "success": "Informações atualizadas com sucesso"
    }
  }
}
</i18n>
